import React, { useEffect, useState } from 'react'
import { useFetch, usePost, usePut } from '../../../components/component-items/helpers'
import GlobalStyles from '../../../components/component-items/styles'
import { Form, Modal, Badge, Tabs, Tab, Card } from 'react-bootstrap'
import { globalState } from '../../../store'
import { EDI_API_ENDPOINTS } from '../../../api/api_endpoints'
import { EDIRetailer, GS1LabelType } from '../../../types/edi_types'

export const EDIRetailerReferenceModal = ({ open, setOpen, retailer }: any) => {
  const {
    state: { csrf },
  } = globalState()

  const [validated, setValidated] = useState(false)
  const [retailerReference, setRetailerReference] = useState(retailer || {})
  const [gs1LabelTypes, setGs1LabelTypes] = useState<GS1LabelType[]>([])
  const [ediRetailers, setEDIRetailers] = useState<EDIRetailer[]>([])
  const [filteredGs1Labels, setFilteredGs1Labels] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState('orderful')

  useEffect(() => {
    if (retailer) {
      const matchingRetailer = ediRetailers.find((r) => r.name === retailer.ediRetailerName)
      setRetailerReference({
        ...retailer,
        ediRetailer: matchingRetailer?.id,
      })
    } else {
      setRetailerReference({})
    }
  }, [open, ediRetailers])

  const { response: ediRetailersResp } = useFetch(`${EDI_API_ENDPOINTS.getRetailers}?limit=1000`, {})
  useEffect(() => {
    if (ediRetailersResp?.results) {
      setEDIRetailers(ediRetailersResp.results)
    }
  }, [ediRetailersResp])

  const { response: gs1LabelTypesResp } = useFetch(`${EDI_API_ENDPOINTS.getGs1LabelTypes}?limit=100`, {})
  useEffect(() => {
    if (gs1LabelTypesResp && gs1LabelTypesResp.results) {
      setGs1LabelTypes(gs1LabelTypesResp.results)
    }
  }, [gs1LabelTypesResp])

  useEffect(() => {
    if (retailerReference.ediRetailer) {
      const labels = gs1LabelTypes
        .filter((label) => label.retailerId === parseInt(retailerReference.ediRetailer))
        .map((label) => label.name)
      setFilteredGs1Labels(labels)
    } else {
      setFilteredGs1Labels([])
    }
  }, [retailerReference.ediRetailer, gs1LabelTypes])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement | HTMLButtonElement | HTMLAnchorElement>) => {
    const form: any = event.currentTarget

    event.preventDefault()
    event.stopPropagation()
    setValidated(true)

    if (form?.checkValidity() === false || !retailerReference.ediRetailer) {
      return
    }

    if (retailerReference.id) {
      usePut(EDI_API_ENDPOINTS.updateRetailer(retailerReference.id), retailerReference, csrf)
    } else {
      usePost(EDI_API_ENDPOINTS.createRetailer, retailerReference, csrf)
    }

    // Close the modal after submission
    setOpen(false)
  }
  return (
    <Modal show={open} onHide={() => setOpen(false)} size="lg" centered>
      <Form validated={validated} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{retailer ? 'Edit Retailer Reference' : 'Add Retailer Reference'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ margin: '0 auto', maxWidth: 600 }}>
            <Form.Group className="required">
              <Form.Label style={{ display: 'inline-flex' }}>
                <GlobalStyles.Tooltip
                  className="subheader__button button"
                  data-title="Select the EDI Retailer Name"
                  style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                >
                  Retailer
                </GlobalStyles.Tooltip>
              </Form.Label>
              <Form.Select
                value={retailerReference.ediRetailer || ''}
                isInvalid={validated && !retailerReference.ediRetailer}
                onChange={(e) => setRetailerReference((s: any) => ({ ...s, ediRetailer: e.target.value }))}
              >
                <option hidden>-- Select --</option>
                {ediRetailers.map(({ id, name }) => (
                  <option value={id} key={id}>
                    {name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">Please select a valid EDI Retailer.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="required" style={{ marginTop: '1em' }}>
              <Form.Label style={{ display: 'inline-flex' }}>
                <GlobalStyles.Tooltip
                  className="subheader__button button"
                  data-title="Found in your routing guidelines."
                  style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                >
                  Retailer Reference ID
                </GlobalStyles.Tooltip>
              </Form.Label>
              <Form.Control
                required
                type="text"
                placeholder="Reference ID"
                value={retailerReference.referenceId}
                onChange={(e) => setRetailerReference((s: any) => ({ ...s, referenceId: e.target.value }))}
              />
              <Form.Control.Feedback type="invalid">
                Please enter the reference ID of the retailer.
              </Form.Control.Feedback>
            </Form.Group>

            {retailerReference.ediRetailer && (
              <div style={{ marginTop: '1.5em' }}>
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'orderful')} className="mb-3" fill>
                  <Tab eventKey="orderful" title="GS1 Labels Settings">
                    <Card className="mt-3">
                      <Card.Body>
                        <Form.Group>
                          {filteredGs1Labels.length > 0 && (
                            <div>
                              <Form.Label>Supported GS1 Labels</Form.Label>
                              <div>
                                {filteredGs1Labels.map((label, index) => (
                                  <Badge key={index} bg="primary" style={{ marginRight: 5, marginBottom: 5 }}>
                                    {label}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey="sps" title="Packing Slips Settings">
                    <Card className="mt-3">
                      <Card.Body>
                        <Form.Group>
                          <Form.Label>
                            Packing Slip Template ID
                            <span className="text-muted ms-2">(used for SPS Commerce documents)</span>
                          </Form.Label>
                          <div className="form-control-plaintext text-break">
                            {retailerReference.spsPackingSlipTemplateId ? (
                              <>
                                <strong>{retailerReference.spsPackingSlipTemplateId}</strong>
                              </>
                            ) : (
                              <span className="text-muted">Not set – please configure in Admin portal</span>
                            )}
                          </div>
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  </Tab>
                </Tabs>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <GlobalStyles.Button style={{ minWidth: 125 }}>Save</GlobalStyles.Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
